import { Controllers } from "@infoland/iprova-api";
import {
    AiAnswer,
    AiSearchQuestionResponse,
    AiSearchQuestionRequest,
    AiSearchResultCache,
    AiSetAnswerForResultRequest,
    AiFeedbackRequest,
    AiCacheSearchResultItem,
    AiCacheResult
} from "@/types/ai/aiSearch";
import { AiResultGetDto, AiResultItemGetDto } from "@/types/ai/aiShared";
import { ActionTree, MutationTree, GetterTree } from "vuex";
import { RootState } from "../..";

interface State {
    clientPageSize: number;
    currentSearchQueries: object;
    searchResults: AiSearchResultCache;
}

/** ===================================================================================================
 *  ↓ Utility functions
 *  ===================================================================================================
 */

/**
 * Create simple search query hash to cache the search results
 */
const encode = (queryObject: object): string => {
    const query = JSON.stringify(queryObject);
    return query;
};

const decode = (hash: string): { searchText: string; collectionId: number; portalId: number } => {
    return JSON.parse(hash);
};

/** ===================================================================================================
 *  ↓ Store (Cache) Functions
 *  ===================================================================================================
 */

function initialize(): State {
    return {
        //current search queries
        clientPageSize: 10,
        currentSearchQueries: {
            in_portal: null,
            outside_portal: null,
            collection: null
        },
        searchResults: {} as AiSearchResultCache
    };
}

const getters: GetterTree<State, any> = {
    getFeedbackScope: (state: State) => (searchScope: "in_portal" | "outside_portal" | "collection", contentItemId: string | null) => {
        const currentSearchEncodedQuery = state.currentSearchQueries[searchScope];
        const currentSearchQuery = decode(currentSearchEncodedQuery);
        const searchResults = state.searchResults[state.currentSearchQueries[searchScope]]?.items;

        return {
            searchText: currentSearchQuery.searchText,
            portalId: currentSearchQuery.portalId,
            questionId: searchResults.find(f => f.content_item_id === contentItemId)?.answer?.question_id ?? null
        };
    },
    getCollectionFilter: (state: State) => {
        const currentSearchEncodedQuery = state.currentSearchQueries["collection"];
        const currentSearchQuery = decode(currentSearchEncodedQuery);
        return currentSearchQuery.collectionId;
    },
    getCollectionIds: (state: State) => (scope: "all" | "collection") => {
        if (scope == "collection") {
            const collectionFilterIds = state.searchResults[state.currentSearchQueries["collection"]]?.collection_ids ?? [];
            return collectionFilterIds;
        }

        const inCurrentPortalCollectionIds = state.searchResults[state.currentSearchQueries["in_portal"]]?.collection_ids ?? [];
        const outsideCurrentPortalCollectionIds = state.searchResults[state.currentSearchQueries["outside_portal"]]?.collection_ids ?? [];
        return [...inCurrentPortalCollectionIds, ...outsideCurrentPortalCollectionIds];
    },
    getItemsToShow: (state: State) => (
        searchScope: "in_portal" | "outside_portal" | "collection",
        searchText: string,
        portalId: number | null,
        collectionId: number | null
    ) => {
        const currentSearchEncodedQuery = encode({
            searchText: searchText,
            searchScope: searchScope,
            portalId: portalId,
            collectionId: collectionId
        });
        return state.searchResults[currentSearchEncodedQuery]?.items_to_show;
    },
    areAllItemsLoaded: (state: State) => (searchScope: "in_portal" | "outside_portal" | "collection") => {
        const currentSearchEncodedQuery = state.currentSearchQueries[searchScope];

        const nothingMoreToShow = state.searchResults[currentSearchEncodedQuery]?.continuation_token === null;
        const itemsLoaded = state.searchResults[currentSearchEncodedQuery]?.items.length > 0;
        return nothingMoreToShow && itemsLoaded;
    },
    getPageSize: (state: State) => {
        return state.clientPageSize;
    }
};

const mutations: MutationTree<State> = {
    cacheSearchResults(
        state: State,
        payload: { searchScope: "in_portal" | "outside_portal" | "collection"; showMore: boolean; result: AiCacheResult }
    ) {
        const currentSearchEncodedQuery = state.currentSearchQueries[payload.searchScope];

        //if we loaded more items, append the new items to the current items
        const noMoreItemsToLoad = payload.result.continuation_token === undefined;
        if (payload.showMore) {
            state.searchResults[currentSearchEncodedQuery].items = [...state.searchResults[currentSearchEncodedQuery].items, ...payload.result.items];
            state.searchResults[currentSearchEncodedQuery].continuation_token = noMoreItemsToLoad ? null : payload.result.continuation_token;
            return;
        }

        //if the page is the same as the current page, replace the results with the new results
        state.searchResults[currentSearchEncodedQuery] = payload.result;
        state.searchResults[currentSearchEncodedQuery].continuation_token = noMoreItemsToLoad ? null : payload.result.continuation_token;
    },
    setCurrentCacheKey(
        state: State,
        payload: {
            searchText: string;
            searchScope: "in_portal" | "outside_portal" | "collection";
            portalId: number;
            collectionId: number | null;
            itemsToShow: number;
        }
    ) {
        const currentSearchEncodedQuery = encode({
            searchText: payload.searchText,
            searchScope: payload.searchScope,
            portalId: payload.portalId,
            collectionId: payload.collectionId
        });
        state.currentSearchQueries[payload.searchScope] = currentSearchEncodedQuery;
    },
    setAIAnswerForResult(state: State, payload: AiSetAnswerForResultRequest) {
        const currentSearchEncodedQuery = state.currentSearchQueries[payload.searchScope];
        const index = state.searchResults[currentSearchEncodedQuery]?.items.findIndex(
            (item: AiCacheSearchResultItem) => item?.content_item_id === payload?.content_item_id
        );
        if (index < 0 || state.searchResults[currentSearchEncodedQuery].items == null) return;
        state.searchResults[currentSearchEncodedQuery].items[index].answer = payload.answer;
        state.searchResults[currentSearchEncodedQuery].items[index].should_generate_answer = false;
    },
    setItemsToShow(state: State, payload: { scope: "in_portal" | "outside_portal" | "collection"; itemsToShow: number }) {
        const currentSearchEncodedQuery = state.currentSearchQueries[payload.scope];
        if (payload.itemsToShow == null) return;

        const currentQueryResults = state.searchResults[currentSearchEncodedQuery];
        const loadedAllResults =
            currentQueryResults != null &&
            (currentQueryResults.items_to_show > currentQueryResults.items.length || currentQueryResults.items.length == 0) &&
            currentQueryResults.continuation_token === null;

        if (loadedAllResults) return;
        //set the items to items_to_show, but if the items_to_show is more than the total items, set it to the total items
        state.searchResults[currentSearchEncodedQuery].items_to_show = Math.min(payload.itemsToShow, currentQueryResults.items.length);
    },
    clearCache(state: State, searchScope: string) {
        const currentSearchEncodedQuery = state.currentSearchQueries[searchScope];
        state.searchResults[currentSearchEncodedQuery] = {
            items: [],
            collection_ids: [],
            continuation_token: undefined,
            items_to_show: null,
            total_items: null
        };
    }
};

const actions: ActionTree<State, RootState> = {
    async loadAiSearchResultsAsync(
        { commit, state },
        payload: {
            question: string;
            searchScope: "in_portal" | "outside_portal" | "collection";
            portalId: Number | null;
            collectionId: number | null;
            showMore: boolean;
            disableCache: boolean;
        }
    ): Promise<{ status: number; results: AiResultItemGetDto[] }> {
        //set current search text to current question
        const newSearchEncodedQuery = {
            searchText: payload.question,
            searchScope: payload.searchScope,
            portalId: payload.portalId,
            collectionId: payload.collectionId
        };
        const currentSearchEncodedQuery = encode(newSearchEncodedQuery);
        const currentState = state.searchResults[currentSearchEncodedQuery];
        commit("setCurrentCacheKey", { ...newSearchEncodedQuery });

        //if same question is searched again, return the previous results
        const containsQueryInCache = currentState != null && currentState.items.length > 0;
        if (!payload.disableCache && containsQueryInCache) {
            return { status: 200, results: state.searchResults[currentSearchEncodedQuery].items };
        }

        //if all items are loaded, and is not existing in cache, return nothing
        const allItemsLoaded = currentState != null && currentState?.continuation_token === null;
        if (allItemsLoaded) {
            return { status: 200, results: [] };
        }

        //retreive search results and store them in the state
        try {
            const searchParams: any = {
                searchText: payload.question,
                searchScope: payload.searchScope,
                portalId: payload.portalId,
                collectionId: payload.collectionId?.toString()
            };

            if (payload.showMore || payload.portalId == null) delete searchParams.portalId;
            if (payload.showMore || payload.question == "") delete searchParams.searchText;
            if (payload.showMore || payload.collectionId == null) delete searchParams.collectionId;
            if (payload.showMore || payload.searchScope == "collection") delete searchParams.searchScope;

            const continuationToken = state.searchResults[currentSearchEncodedQuery]?.continuation_token;
            if (payload.showMore && continuationToken != null) {
                searchParams.continuationToken = continuationToken;
            }

            const requestUrl = `search?${new URLSearchParams(searchParams).toString()}`;
            let data = await Controllers.GenericController.getAsync<AiResultGetDto<AiCacheSearchResultItem>>(requestUrl, false);

            //add items_to_show to the result
            data = {
                items: data.items.map(item => {
                    return { ...item, answer: undefined, in_current_portal: payload.searchScope === "in_portal" };
                }),
                collection_ids: data.collection_ids,
                continuation_token: data.continuation_token,
                items_to_show: null,
                total_items: data.total_items
            } as AiCacheResult;

            commit("cacheSearchResults", { searchScope: payload.searchScope, showMore: payload.showMore, result: data });

            //return new search results
            return { status: 200, results: data.items };
        } catch (error: any) {
            //if the search results are being to old, clear the initial results
            if ((error as any).response.status == 404) {
                return { status: 404, results: [] };
            }
            return { status: 500, results: [] };
        }
    },
    async loadMore({ dispatch, state }, payload: { searchScope: "in_portal" | "outside_portal" | "collection" }) {
        const currentSearchEncodedQuery = state.currentSearchQueries[payload.searchScope];
        const currentSearchQuery = decode(currentSearchEncodedQuery);

        const results = await dispatch("loadAiSearchResultsAsync", {
            question: currentSearchQuery.searchText,
            searchScope: payload.searchScope,
            portalId: currentSearchQuery.portalId,
            collectionId: currentSearchQuery.collectionId,
            showMore: true,
            disableCache: true
        });
        return results;
    },
    async loadAiAnswerAsync({ commit }, answerRequest: AiSearchQuestionRequest): Promise<AiAnswer | undefined> {
        if (answerRequest == null || answerRequest?.question == null || answerRequest?.content_item_id == null) {
            throw new Error("LoadAiAnswerAsync: Invalid request parameters. question and documentId are required.");
        }
        //post the question
        let question = null;
        try {
            question = await Controllers.GenericController.postAsync<{ question: string; content_item_id: string }, AiSearchQuestionResponse>(
                "search/question",
                {
                    question: answerRequest?.question,
                    content_item_id: answerRequest?.content_item_id
                },
                false
            );
        } catch (error) {
            if ((error as any).response.status == 404) return null;
        }

        const created_identifier = question.created_identifier;
        //retreive the answer and return it
        let answer = await Controllers.GenericController.getAsync<AiAnswer>(`search/question/${created_identifier}`);
        if (answer != null)
            commit("setAIAnswerForResult", {
                searchScope: answerRequest.searchScope,
                content_item_id: answerRequest.content_item_id,
                answer: answer
            });
        return answer;
    },
    postAiFeedback(_, payload: AiFeedbackRequest) {
        Controllers.GenericController.postAsync<AiFeedbackRequest, { created_identifier: string }>("search/feedback", payload);
    }
};

const state: State = initialize();

// export the namespace
export const namespace = "search";

// export the store 'module'
export default {
    namespaced: true,
    state,
    mutations,
    actions,
    getters
};
